<template>
    <section class="relative z-20 overflow-hidden grid place-items-center sm:min-h-[720px] max-sm:mt-20">
        <!-- Two-wall tunnel backdrop (reusable) — hidden on mobile -->
        <WrGenicTunnel class="max-lg:hidden" />

        <!-- Flowing prompts (enter the left arc) + components (emerge from the right arc) -->
        <div class="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
            <div class="max-lg:hidden">
                <!-- Prompts: masked so they vanish at the left arc's bright edge -->
                <div
                    class="absolute inset-0 [mask-image:linear-gradient(to_right,#000_16%,transparent_26%)] [-webkit-mask-image:linear-gradient(to_right,#000_16%,transparent_26%)]">
                    <div v-for="(prompt, index) in heroPrompts" :key="index"
                        class="hero-prompt-flow absolute flex items-center gap-2 rounded-full px-4 py-2 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.7)]"
                        :class="heroTintClasses[prompt.tint]"
                        :style="{ left: prompt.left, top: prompt.top, 'animation-delay': prompt.delay }">
                        <span class="size-2 rounded-full flex-shrink-0" :style="{ background: prompt.dot }"></span>
                        <p class="text-[13px] whitespace-nowrap text-white dark:text-[#c9cdeb]">{{ prompt.text }}</p>
                    </div>
                </div>

                <!-- Components: masked so they only appear past the right arc's bright edge -->
                <div
                    class="absolute inset-0 [mask-image:linear-gradient(to_right,transparent_73%,#000_81%)] [-webkit-mask-image:linear-gradient(to_right,transparent_73%,#000_81%)]">
                    <div style="animation-delay: -1.5s"
                        class="hero-component-flow absolute right-[6%] top-[30%] w-[230px] rounded-2xl p-3.5 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.8)]"
                        :class="heroTintClasses.green">
                        <div class="flex items-start justify-between">
                            <div class="flex items-center gap-2">
                                <WrIcon :name="weatherCard.icon"
                                    class="size-5 text-[#B45309] dark:text-[#FCD34D] flex-shrink-0" />
                                <div>
                                    <p class="text-base font-medium leading-tight text-white">
                                        {{ weatherCard.city }}
                                    </p>
                                    <p class="text-[11px] text-white dark:text-[#8B90B5]">
                                        {{ weatherCard.condition }}
                                    </p>
                                </div>
                            </div>
                            <p class="text-2xl text-white">{{ weatherCard.temp }}</p>
                        </div>
                    </div>

                    <div style="animation-delay: -3.3s"
                        class="hero-component-flow absolute right-[6%] top-[47%] w-[240px] rounded-2xl p-3.5 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.8)]"
                        :class="heroTintClasses.blue">
                        <h3 class="text-sm font-medium text-white">
                            {{ orderCard.title }}
                        </h3>
                        <div class="mt-2.5 flex items-start justify-between px-1">
                            <template v-for="(step, i) in orderCard.steps" :key="i">
                                <div class="flex flex-col items-center gap-1.5">
                                    <span
                                        class="relative size-4 rounded-full grid place-items-center flex-shrink-0 after:content-[''] after:size-[7px] after:rounded-full after:bg-white"
                                        :class="heroStepClasses[i]"></span>
                                    <span class="text-[10px] text-white dark:text-[#C9CDEB]">
                                        {{ step }}
                                    </span>
                                </div>
                                <span v-if="i < orderCard.steps.length - 1"
                                    :class="['flex-1 h-[2px] mt-[7px] mx-1', i === 0 ? 'bg-[linear-gradient(90deg,#a855f7,#6d5df6)]' : 'bg-[#3a3f6e]']"></span>
                            </template>
                        </div>
                        <div class="mt-3 flex items-center gap-2.5 rounded-xl border border-[rgba(124,108,246,0.35)] bg-[rgba(24,31,74,0.5)] py-1.5 px-3">
                            <WrIcon :name="orderCard.etaIcon" class="size-4 text-[#67E8F9] flex-shrink-0" />
                            <p class="text-xs font-semibold text-white">
                                {{ orderCard.etaText }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- History card: own mask shifted right so its reveal hugs the
                     wall's curved edge at this lower height (the shared straight
                     mask above would reveal it early, in open space) -->
                <div
                    class="absolute inset-0 [mask-image:linear-gradient(to_right,transparent_77%,#000_85%)] [-webkit-mask-image:linear-gradient(to_right,transparent_77%,#000_85%)]">
                    <div style="animation-delay: -5.1s"
                        class="hero-component-flow absolute right-[5%] top-[70%] w-[210px] rounded-2xl p-3.5 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.8)]"
                        :class="heroTintClasses.purple">
                        <div class="flex items-center justify-between">
                            <p class="text-sm font-medium text-white">
                                {{ historyCard.title }}
                            </p>
                            <span class="px-3 py-[3px] rounded-full bg-[#7c5cfc] text-white text-xs font-medium leading-none">{{
                                historyCard.badge }}</span>
                        </div>
                        <div v-for="(item, i) in historyCard.items" :key="i"
                            class="rounded-xl border border-[rgba(124,108,246,0.3)] bg-[rgba(24,31,74,0.5)] py-1.5 px-3"
                            :class="i === 0 ? 'mt-2' : 'mt-1.5'">
                            <div class="flex items-center justify-between">
                                <p class="text-[11px] leading-none text-[#8B90B5]">
                                    {{ item.id }}
                                </p>
                                <span class="px-2 py-px rounded-full border border-[rgba(124,108,246,0.35)] bg-[rgba(19,25,58,0.9)] text-[#8b90b5] text-[11px] leading-none">{{
                                    item.status }}</span>
                            </div>
                            <p class="mt-0.5 text-xs text-white">
                                {{ item.note }}
                            </p>
                            <p class="text-[10px] leading-none text-[#8B90B5]">
                                {{ item.created }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="relative z-10 flex flex-col items-center gap-2 mx-auto max-w-[787px] px-4 w-full">
            <div
                class="genic-hero-badge max-w-[432px] max-sm:max-w-full w-full rounded-[60px] border border-[#885AF51F] bg-[#885AF51A] dark:border-[#FFFFFF14] dark:bg-[#885AF52E] flex flex-row items-center justify-center gap-[10px] max-sm:gap-1 py-2 mb-4">
                <div class="size-[6px] bg-[#935AF5] rounded-full" />
                <p class="font-avant font-medium text-sm max-sm:text-[12px] text-[#6D28D9] dark:text-[#AA9AFF] text-center pr-1">
                    {{ badgeText }}
                </p>
            </div>
            <div class="text-center">
                <WrGenicUiLogo
                    role="img"
                    aria-label="Genic UI"
                    class="h-10 w-auto lg:h-12 xl:h-16 text-[#9F86FF] dark:text-[#C4B5FD]" />
            </div>
            <h1 class="text-center mb-2">
                <WrGradientText :text="headingLine1" append-class=" p-0 leading-[1.3]" />
                <p class="leading-[1.30]">{{ headingLine2 }}</p>
            </h1>
            <p class="font-normal text-lg lg:text-2xl xl:px-20 text-center text-[#5A5478] dark:text-[#CBCBCB]">
                {{ description }}
            </p>
            <div class="flex flex-wrap w-full justify-center items-center gap-[18px] mt-4">
                <WrButton container-class="bg-[linear-gradient(95.69deg,#8F8AF2_2.69%,#3838A6_113.37%)]"
                    append-class="!bg-[linear-gradient(95.69deg,#5B54E8_2.69%,#3B35B5_113.37%)]"
                    link="https://genicui.vaah.ai/">{{ ctaText }}
                </WrButton>
                <WrButton container-class="genic-hero-outline bg-none"
                    append-class="!bg-none dark:text-white text-[#1E1B4B]"
                    @click="$emit('demo-clicked')">
                    <span class="inline-flex items-center gap-2">
                        <WrVideoPlay class="size-4 text-current" />
                        {{ demoCtaText }}
                    </span>
                </WrButton>
            </div>
            <div class="flex items-center gap-8 text-sm max-sm:text-xs font-medium mt-4">
                <template v-for="link in redirectLinks">
                    <a :href="link.link" target="_blank" rel="noopener" class="flex items-center gap-[6px]">
                        <component :is="link.icon" v-if="link.icon" class="size-[25px]" />
                        <p>{{ link.text }}</p>
                    </a>
                </template>
            </div>
        </div>
    </section>
</template>

<script setup>
import WrArrowUpRight from "~/components/molecules/WrArrowUpRight.vue";
import WrGithub from "~/components/molecules/WrGithub.vue";
import WrGenicTunnel from "~/components/molecules/WrGenicTunnel.vue";
import WrVideoPlay from "~/components/molecules/WrVideoPlay.vue";
import WrGenicUiLogo from "~/components/molecules/WrGenicUiLogo.vue";

// Tint + step color classes (moved from scoped CSS to inline Tailwind)
const heroTintClasses = {
    green: "border-[0.9px] border-[#FFFFFF21] bg-[#1CC56E66] dark:bg-[#1CC56E3B]",
    blue: "border-[0.9px] border-[#FFFFFF21] bg-[#571CC580] dark:bg-[#571CC56E]",
    purple: "border-[0.9px] border-[#FFFFFF21] bg-[#981CC580] dark:bg-[#981CC56E]",
};
const heroStepClasses = [
    "bg-[#a855f7] shadow-[0_0_12px_rgba(168,85,247,0.8)]",
    "bg-[#6d5df6] shadow-[0_0_10px_rgba(109,93,246,0.7)]",
    "bg-transparent border-2 border-[#34d399] after:hidden",
];

defineEmits(["demo-clicked"]);

defineProps({
    badgeText: {
        type: String,
        default: "FLAGSHIP PRODUCT BY WEBREINVENT AI SOFTWARE FACTORY",
    },
    headingLine1: {
        type: String,
        default: "Stop Reading Answers",
    },
    headingLine2: {
        type: String,
        default: "Start using them",
    },
    description: {
        type: String,
        default:
            "GenicUI Developed by Webreinvent to turn flat AI responses into live, click-ready tools and dynamic web components instantly.",
    },
    ctaText: {
        type: String,
        default: "Explore GenicUI",
    },
    demoCtaText: {
        type: String,
        default: "Watch Demo",
    },
    redirectLinks: {
        type: Array,
        default: () => [
            {
                icon: WrGithub,
                text: "Open Source",
                link: "https://github.com/vaah-ai/GenicUI",
            },
            {
                icon: WrArrowUpRight,
                text: "Documentation",
                link: "https://genicui.vaah.ai/getting-started/introduction",
            },
        ],
    },
    heroPrompts: {
        type: Array,
        default: () => [
            {
                text: "What's the weather in San Francisco",
                dot: "#34d399",
                left: "5%",
                top: "34%",
                delay: "-0.5s",
                tint: "green",
            },
            {
                text: "Show my recent order status",
                dot: "#60A5FA",
                left: "9%",
                top: "50%",
                delay: "-2.3s",
                tint: "blue",
            },
            {
                text: "Show my recent complaint status",
                dot: "#a78bfa",
                left: "4%",
                top: "66%",
                delay: "-4.1s",
                tint: "purple",
            },
        ],
    },
    weatherCard: {
        type: Object,
        default: () => ({
            icon: "streamline:interface-weather-sun-photos-light-camera-mode-brightness-sun-photo-full",
            city: "San Francisco",
            condition: "Sunny · Wind 6 mph",
            temp: "64°",
        }),
    },
    orderCard: {
        type: Object,
        default: () => ({
            title: "Order #23147",
            steps: ["Processing", "Shipped", "Delivered"],
            etaIcon: "streamline:shipping-truck",
            etaText: "Arriving Tomorrow",
        }),
    },
    historyCard: {
        type: Object,
        default: () => ({
            title: "Order History",
            badge: "Open",
            items: [
                {
                    id: "#4820",
                    status: "Closed",
                    note: "Refund not initiated.",
                    created: "Created 14 days ago",
                },
                {
                    id: "#4819",
                    status: "Closed",
                    note: "Order not received.",
                    created: "Created 2 months ago",
                },
            ],
        }),
    },
});
</script>

<style scoped>
/* Rotating conic-gradient ring (hero badge + Watch Demo CTA) — needs @property + mask-composite */
@property --genic-ring-angle {
    syntax: "<angle>";
    initial-value: 0deg;
    inherits: false;
}

.genic-hero-badge {
    position: relative;
}

.genic-hero-badge::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    padding: 2px;
    background: conic-gradient(from var(--genic-ring-angle),
            transparent 0deg,
            transparent 320deg,
            #D9D9D9 345deg,
            transparent 360deg);
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    animation: genic-ring-rotate 8s linear infinite;
    pointer-events: none;
}

@keyframes genic-ring-rotate {
    to {
        --genic-ring-angle: 360deg;
    }
}

.my-app-light .genic-hero-badge::after {
    background: conic-gradient(from var(--genic-ring-angle),
            transparent 0deg,
            transparent 320deg,
            #885AF5 345deg,
            transparent 360deg);
}

/* Static gradient border ring on the outline CTA (mask-composite) */
.genic-hero-outline {
    position: relative;
}

.genic-hero-outline::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    padding: 1px;
    background: linear-gradient(95.69deg, #B1B5FF 2.69%, #3838A6 113.37%);
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
}

/* Rotating conic-gradient sweep, layered above the static ring */
.genic-hero-outline::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    padding: 1px;
    background: conic-gradient(from var(--genic-ring-angle),
            transparent 0deg,
            transparent 320deg,
            #D9D9D9 345deg,
            transparent 360deg);
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    animation: genic-ring-rotate 8s linear infinite;
    pointer-events: none;
}

.my-app-light .genic-hero-outline::after {
    background: conic-gradient(from var(--genic-ring-angle),
            transparent 0deg,
            transparent 320deg,
            #885AF5 345deg,
            transparent 360deg);
}

/* Motion */
@keyframes hero-prompt-flow {
    0% {
        opacity: 0;
        transform: translateX(0) scale(1);
    }

    12% {
        opacity: 1;
    }

    100% {
        opacity: 1;
        transform: translateX(300px) scale(0.7);
    }
}

.hero-prompt-flow {
    animation: hero-prompt-flow 5.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes hero-component-flow {
    0% {
        opacity: 1;
        transform: translateX(-300px) scale(0.7);
    }

    70% {
        opacity: 1;
    }

    100% {
        opacity: 0;
        transform: translateX(0) scale(1);
    }
}

.hero-component-flow {
    animation: hero-component-flow 5.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@media (prefers-reduced-motion: reduce) {

    .hero-prompt-flow,
    .hero-component-flow {
        animation: none;
        opacity: 1;
    }

    .genic-hero-badge::after,
    .genic-hero-outline::after {
        animation: none;
    }
}
</style>
